import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { eventTypesAPI, bookingsAPI } from "../services/schedulerAPI";
import { showToast } from "../components/Toast";

const SchedulerDashboard = () => {
  const [eventTypes, setEventTypes] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [eventsData, bookingsData] = await Promise.all([
        eventTypesAPI.list(),
        bookingsAPI.list({ status: "confirmed", upcoming: true }),
      ]);

      setEventTypes(eventsData.filter((e) => e.is_active));
      setUpcomingBookings(bookingsData.slice(0, 10)); // Show first 10
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
      showToast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = (eventType) => {
    const link = `${window.location.origin}/book/${eventType.slug || eventType.id}`;
    navigator.clipboard.writeText(link);
    showToast.success("Link copied to clipboard!");
  };

  const getConferenceIcon = (provider) => {
    switch (provider) {
      case "google_meet":
        return (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M22.5 8.25v7.5c0 .83-.67 1.5-1.5 1.5H3c-.83 0-1.5-.67-1.5-1.5v-7.5c0-.83.67-1.5 1.5-1.5h18c.83 0 1.5.67 1.5 1.5zM12 14l-9-5.5v9l9-3.5z" />
          </svg>
        );
      case "zoom":
        return "📹";
      default:
        return "📍";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Nikesh Masiwal
              </h1>
              <a
                href={`${window.location.origin}/book/nikesh`}
                className="text-blue-600 hover:underline text-sm"
              >
                {window.location.origin}/book/nikesh
              </a>
            </div>
            <Link
              to="/scheduler/links/new"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <span className="text-xl">+</span>
              <span>New Link</span>
            </Link>
          </div>
        </div>

        {/* My invites section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-2">
              <svg
                className="w-5 h-5 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              <h2 className="text-xl font-semibold text-gray-900">
                My invites
              </h2>
            </div>
            <Link
              to="/scheduler/links"
              className="text-blue-600 hover:underline text-sm"
            >
              View all invites
            </Link>
          </div>

          {eventTypes.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">
                You don't have any event types yet.
              </p>
              <Link
                to="/scheduler/links/new"
                className="text-blue-600 hover:underline"
              >
                Create your first event type
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {eventTypes.map((eventType) => (
                <motion.div
                  key={eventType.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-5 h-5 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      <h3 className="font-medium text-gray-900">
                        {eventType.name}
                      </h3>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span>{eventType.duration_minutes} min</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      {getConferenceIcon(eventType.conference_provider)}
                      <span>
                        {eventType.conference_provider === "google_meet"
                          ? "Google Meet"
                          : eventType.conference_provider === "zoom"
                          ? "Zoom"
                          : eventType.location || "No location"}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleCopyLink(eventType)}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Copy link"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => showToast.info("Event type editing coming soon! For now, create a new event type from the links page.")}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Edit"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                    <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                        />
                      </svg>
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming events section */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              Upcoming AgentGarden events
            </h2>
            <Link
              to="/scheduler/events"
              className="text-blue-600 hover:underline text-sm"
            >
              View all events
            </Link>
          </div>

          {upcomingBookings.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">
                You don't have any upcoming AgentGarden events. Share your scheduling
                links with guests. As events are scheduled, they'll appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {upcomingBookings.map((booking) => (
                <div
                  key={booking.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {booking.guest_name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {new Date(booking.start_time).toLocaleString()}
                      </p>
                    </div>
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                      Confirmed
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchedulerDashboard;
